/* ==========================================================================
   sk-live-search.js
   --------------------------------------------------------------------------
   Native live-search dropdown — replaces Empire's .live-search--takeover.

   Flow:
   1. User types in input → debounced (180 ms) fetch to /search/suggest.json
   2. Results rendered as a compact dropdown BELOW the input (not fullscreen)
   3. Keyboard nav: ArrowDown/Up to move focus, Enter to go, Escape to close
   4. Clear button (X) visible when input has content
   5. Click outside → close dropdown

   Uses Shopify's Predictive Search API. Supports DE/FR/IT/PL/NL automatically
   via request locale. No dependency on Empire's live-search system.

   Horizon-Port 30.10.2026: unverändert aus Empire (gleiche Endpunkte /search/suggest.json,
   /products/<handle>.js für den Grundpreis, assets/lexikon-index.json), geladen als ES-Modul
   (type="module", fetchpriority="low") aus sections/sk-header.liquid. Ersetzt auch
   sk-mobile-search.js (das steuerte nur Empires alte .live-search und entfällt).
   ========================================================================== */
(function () {
  'use strict';
  if (typeof document === 'undefined') return;

  function boot() {
    var root = document.querySelector('[data-sk-live-search]');
    if (!root) return;

    var input = root.querySelector('[data-sk-live-search-input]');
    var dropdown = root.querySelector('[data-sk-live-search-dropdown]');
    var clearBtn = root.querySelector('[data-sk-live-search-clear]');
    var form = root.querySelector('[data-sk-live-search-form]');
    var termsTemplate = root.querySelector('[data-sk-live-search-terms]');
    if (!input || !dropdown || !form) return;

    var currentResults = [];
    var activeIndex = -1;
    var debounceId = null;
    var lastQ = '';

    function close() {
      dropdown.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      activeIndex = -1;
      clearActive();
    }
    function open() {
      dropdown.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    }
    /* Barrierefreiheit (03.11.2026): aktiver Treffer wird dem Screenreader über
       aria-activedescendant + aria-selected gemeldet, nicht nur per CSS-Klasse. */
    function clearActive() {
      var items = dropdown.querySelectorAll('.sk-live-search__item');
      for (var i = 0; i < items.length; i++) { items[i].classList.remove('is-active'); items[i].removeAttribute('aria-selected'); }
      input.removeAttribute('aria-activedescendant');
    }
    function setActive(index) {
      var items = dropdown.querySelectorAll('.sk-live-search__item');
      if (!items.length) return;
      if (index < 0) index = items.length - 1;
      if (index >= items.length) index = 0;
      clearActive();
      var it = items[index];
      if (!it.id) it.id = 'sk-ls-opt-' + index;
      it.classList.add('is-active');
      it.setAttribute('aria-selected', 'true');
      input.setAttribute('aria-activedescendant', it.id);
      it.scrollIntoView({ block: 'nearest' });
      activeIndex = index;
    }
    function escapeHtml(s) {
      return String(s).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }

    // Preis im DE-Format: "19,95 €" statt "19.95"
    function formatPrice(price) {
      if (price == null || price === '') return '';
      var num = parseFloat(String(price).replace(',', '.'));
      if (isNaN(num)) return String(price);
      return num.toFixed(2).replace('.', ',') + ' €';
    }

    // Labels pulled from data-attributes (Liquid renders localized via `| t`)
    var L = {
      products: dropdown.getAttribute('data-label-products') || 'Produkte',
      collections: dropdown.getAttribute('data-label-collections') || 'Kategorien',
      pages: dropdown.getAttribute('data-label-pages') || 'Seiten',
      articles: dropdown.getAttribute('data-label-articles') || 'Artikel',
      viewAll: dropdown.getAttribute('data-label-view-all') || 'Alle Ergebnisse anzeigen →',
      noResults: dropdown.getAttribute('data-label-no-results') || 'Keine Ergebnisse für',
      searching: dropdown.getAttribute('data-label-searching') || 'Suche nach',
      // `| t | default:` can't override Shopify's "Translation missing" string, and
      // locales without the key would render it literally — guard with a JS fallback.
      lexikon: (function (v) { return (!v || /translation missing/i.test(v)) ? 'Lexikon' : v; })(dropdown.getAttribute('data-label-lexikon')),
      noProducts: (function (v) { return (!v || /translation missing/i.test(v)) ? 'Keine passenden Produkte gefunden' : v; })(dropdown.getAttribute('data-label-no-products')),
      related: (function (v) { return (!v || /translation missing/i.test(v)) ? 'Ähnliche Produkte' : v; })(dropdown.getAttribute('data-label-related'))
    };

    function renderSectionLabel(text) {
      return '<div class="sk-live-search__section-label">' + escapeHtml(text) + '</div>';
    }
    function renderProductItem(p) {
      var img = p.featured_image ? p.featured_image.url : (p.image || '');
      var thumbHtml = img
        ? '<img class="sk-live-search__thumb" src="' + escapeHtml(img) + '" alt="" loading="lazy" width="48" height="48">'
        : '<span class="sk-live-search__thumb" aria-hidden="true"></span>';
      return '<a class="sk-live-search__item" role="option" href="' + escapeHtml(p.url) + '">' +
        thumbHtml +
        '<div class="sk-live-search__info">' +
        '<div class="sk-live-search__title">' + escapeHtml(p.title) + '</div>' +
        (p.handle ? '<div class="sk-live-search__unit" data-sk-ls-unit="' + escapeHtml(p.handle) + '" data-sk-ls-price="' + escapeHtml(String(p.price || '')) + '" hidden></div>' : '') +
        '</div>' +
        (p.price ? '<div class="sk-live-search__price">' + escapeHtml(formatPrice(p.price)) + '</div>' : '') +
        '</a>';
    }
    /* Grundpreis (PAngV, 14.10.2026): suggest.json liefert keine Mengenangabe → je Treffer /products/<handle>.js
       (gecacht), Variante zum angezeigten Preis (lieferbare bevorzugt), Text aus window.skUnitPrice (unit-price-fix.liquid). */
    var unitCache = {};
    function fillUnits() {
      if (!window.skUnitPrice || !window.fetch) return;
      var els = dropdown.querySelectorAll('[data-sk-ls-unit]');
      Array.prototype.forEach.call(els, function (el) {
        var h = el.getAttribute('data-sk-ls-unit');
        if (!h) return;
        var cents = Math.round(parseFloat(el.getAttribute('data-sk-ls-price')) * 100);
        if (!unitCache[h]) {
          unitCache[h] = fetch('/products/' + encodeURIComponent(h) + '.js', { credentials: 'same-origin' })
            .then(function (r) { return r.ok ? r.json() : null; })
            .catch(function () { return null; });
        }
        unitCache[h].then(function (prod) {
          if (!prod || !el.isConnected) return;
          var vs = prod.variants || [];
          var v = vs.filter(function (x) { return x.price === cents && x.available; })[0] ||
            vs.filter(function (x) { return x.price === cents; })[0] || vs[0];
          var t = v ? window.skUnitPrice.text(v.unit_price_measurement, v.unit_price, v.title, false) : '';
          if (t) { el.textContent = t; el.hidden = false; }
        });
      });
    }
    function renderArticleItem(a) {
      var img = a.image ? (a.image.url || a.image) : '';
      var thumbHtml = img
        ? '<img class="sk-live-search__thumb" src="' + escapeHtml(img) + '" alt="" loading="lazy" width="48" height="48">'
        : '<span class="sk-live-search__thumb sk-live-search__thumb--doc" aria-hidden="true"></span>';
      return '<a class="sk-live-search__item" role="option" href="' + escapeHtml(a.url) + '">' +
        thumbHtml +
        '<div class="sk-live-search__info">' +
        '<div class="sk-live-search__title">' + escapeHtml(a.title) + '</div>' +
        '</div>' +
        '</a>';
    }
    function renderChips(items) {
      var out = '<div class="sk-live-search__chips">';
      for (var i = 0; i < items.length; i++) {
        out += '<a class="sk-live-search__chip" role="option" href="' + escapeHtml(items[i].url) + '">' +
          escapeHtml(items[i].title) + '</a>';
      }
      return out + '</div>';
    }

    /* ---- Lexikon (species_profile metaobjects) ----------------------------
       Shopify's Predictive Search API can't index metaobjects, so the lexikon
       species would never appear in native results. We load a generated index
       (assets/lexikon-index.json) on first search and append a "Lexikon"
       section below the native results. Index is fetched once, then cached. */
    var lexikonData = null, lexikonPromise = null;
    function lexNormalize(s) {
      if (!s) return '';
      return String(s).toLowerCase()
        .replace(/ß/g, 'ss').replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue')
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .replace(/ae/g, 'a').replace(/oe/g, 'o').replace(/ue/g, 'u');
    }
    function loadLexikon() {
      if (lexikonPromise) return lexikonPromise;
      var url = window.lexikonIndexUrl || (dropdown.getAttribute('data-lexikon-url')) || '/assets/lexikon-index.json';
      lexikonPromise = fetch(url, { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : []; })
        .then(function (j) { lexikonData = Array.isArray(j) ? j : []; return lexikonData; })
        .catch(function () { lexikonData = []; return lexikonData; });
      return lexikonPromise;
    }
    function searchLexikon(term, limit) {
      if (!lexikonData || !term || term.length < 2) return [];
      var lower = term.toLowerCase(), norm = lexNormalize(term), max = limit || 4, out = [];
      for (var i = 0; i < lexikonData.length && out.length < max; i++) {
        var s = lexikonData[i], nd = s.name_de || '', sci = s.scientific_name || '';
        if (nd.toLowerCase().indexOf(lower) !== -1 || lexNormalize(nd).indexOf(norm) !== -1 ||
            sci.toLowerCase().indexOf(lower) !== -1 || lexNormalize(sci).indexOf(norm) !== -1) {
          out.push(s);
        }
      }
      return out;
    }
    function renderLexikonItem(s) {
      var img = s.main_image_url || '';
      var thumb = img
        ? '<img class="sk-live-search__thumb" src="' + escapeHtml(img) + '" alt="" loading="lazy" width="48" height="48">'
        : '<span class="sk-live-search__thumb" aria-hidden="true"></span>';
      var sci = (s.scientific_name && s.scientific_name !== s.name_de)
        ? '<div class="sk-live-search__sci">' + escapeHtml(s.scientific_name) + '</div>' : '';
      return '<a class="sk-live-search__item" role="option" href="' + escapeHtml(s.url) + '">' +
        thumb +
        '<div class="sk-live-search__info">' +
        '<div class="sk-live-search__title">' + escapeHtml(s.name_de || s.scientific_name) + '</div>' + sci +
        '</div></a>';
    }
    function appendLexikon(queryText) {
      loadLexikon().then(function () {
        if (input.value.trim() !== queryText) return; // user kept typing
        if (dropdown.querySelector('[data-sk-lexikon-block]')) return; // already added
        var hits = searchLexikon(queryText, 4);
        if (!hits.length) return;
        var emptyMsg = dropdown.querySelector('.sk-live-search__empty');
        if (emptyMsg) emptyMsg.parentNode.removeChild(emptyMsg);
        var html = '<div data-sk-lexikon-block>' + renderSectionLabel(L.lexikon);
        for (var i = 0; i < hits.length; i++) html += renderLexikonItem(hits[i]);
        html += '</div>';
        var all = dropdown.querySelector('.sk-live-search__all');
        if (all) all.insertAdjacentHTML('beforebegin', html);
        else dropdown.insertAdjacentHTML('beforeend', html);
        open();
      });
    }

    function render(results, queryText) {
      results = results || {};
      var products = results.products || [];
      var collections = results.collections || [];
      var pages = results.pages || [];
      var articles = results.articles || [];
      var total = products.length + collections.length + pages.length + articles.length;

      if (total === 0) {
        dropdown.innerHTML = '<div class="sk-live-search__empty">' + escapeHtml(L.noResults) + ' "' + escapeHtml(queryText) + '"</div>';
        open();
        appendLexikon(queryText); // lexikon species may still match even if no products do
        return;
      }

      var html = '';
      if (products.length) {
        html += renderSectionLabel(results.productsAreRelated ? L.related : L.products);
        for (var i = 0; i < products.length; i++) html += renderProductItem(products[i]);
      } else {
        // Articles/pages matched but no product did — say so instead of leaving
        // the impression that we don't carry a matching product at all.
        html += '<div class="sk-live-search__note">' + escapeHtml(L.noProducts) + '</div>';
      }
      if (collections.length) {
        html += renderSectionLabel(L.collections);
        html += renderChips(collections);
      }
      if (pages.length) {
        html += renderSectionLabel(L.pages);
        html += renderChips(pages);
      }
      if (articles.length) {
        html += renderSectionLabel(L.articles);
        for (var j = 0; j < articles.length; j++) html += renderArticleItem(articles[j]);
      }
      html += '<a class="sk-live-search__all" href="/search?q=' + encodeURIComponent(queryText) + '">' +
        escapeHtml(L.viewAll) + '</a>';

      dropdown.innerHTML = html;
      fillUnits();
      currentResults = products; // keyboard nav focuses product list
      activeIndex = -1;
      open();
      appendLexikon(queryText); // append "Lexikon" section below native results
    }

    function renderLoading(q) {
      dropdown.innerHTML = '<div class="sk-live-search__loading">' + escapeHtml(L.searching) + ' "' + escapeHtml(q) + '"…</div>';
      open();
    }

    /* `resources[limit]` ist ein Gesamtbudget über ALLE angefragten Typen, nicht
       pro Typ: mit type=product,collection,page,article liefert "bettwanzen*" nur
       2 statt 4 Produkte, weil Artikel die Plätze wegnehmen. Deshalb zwei
       parallele Abfragen — Produkte behalten ihre 5 Slots. */
    function suggestUrl(q, types) {
      return '/search/suggest.json?q=' + encodeURIComponent(q) +
        '&resources[type]=' + (types || 'product') +
        '&resources[limit]=5' +
        '&resources[options][unavailable_products]=last' +
        '&resources[options][fields]=title,product_type,vendor,tag,body';
    }

    function resultsOf(data) {
      return (data && data.resources && data.resources.results) || {};
    }

    /* ---- German compounds ---------------------------------------------------
       Shopify's Predictive Search prefix-matches only the LAST word of a query;
       every word before it has to exist as a whole word in the index. So
       "silber spray" never reaches "Silberspray" (0 products, only blog posts),
       while "silberspray" does. Same for "motten falle" → "Mottenfallen".
       When the plain query finds no product, retry with the words glued
       together, then with every word as a prefix. */
    function fallbackQueries(q) {
      var words = q.split(/\s+/).filter(Boolean).map(function (w) { return w.replace(/\*+$/, ''); });
      if (words.length < 2 || words.length > 4) return [];
      var out = [];
      var glued = words.join('');
      if (glued.length <= 40) out.push({ q: glued + '*', related: false });
      out.push({ q: words.map(function (w) { return w + '*'; }).join(' '), related: false });
      return out;
    }

    /* Letzte Stufe: Shopify verknüpft mehrere Wörter mit UND — "bettwanzen mittel"
       findet nichts, obwohl "bettwanzen" für sich 4 Treffer hat. Also je Stichwort
       einzeln fragen (ODER) und beim ersten Wort mit Treffern aufhören. Längstes
       Wort zuerst, weil das spezifischere Wort die besseren Treffer liefert —
       generische Wörter wie "Mittel" liefern Dünger und Ohrreiniger. Über die
       Wortlänge zu gehen spart eine Stopwort-Liste pro Sprache. */
    function singleWordQueries(q) {
      var words = q.split(/\s+/).filter(Boolean).map(function (w) { return w.replace(/\*+$/, ''); });
      if (words.length < 2 || words.length > 4) return [];
      return words
        .filter(function (w) { return w.length >= 3; })
        .sort(function (a, b) { return b.length - a.length; })
        .slice(0, 2)
        .map(function (w) { return { q: w + '*', related: true }; });
    }

    function fetchProducts(q) {
      return fetch(suggestUrl(q))
        .then(function (r) { return r.json(); })
        .then(function (d) { return resultsOf(d).products || []; })
        .catch(function () { return []; });
    }

    // Walk the fallback queries one by one, stop at the first that has products.
    function firstFallbackHit(queries, i) {
      if (i >= queries.length) return Promise.resolve({ products: [], related: false });
      return fetchProducts(queries[i].q).then(function (products) {
        if (products.length) return { products: products, related: queries[i].related };
        return firstFallbackHit(queries, i + 1);
      });
    }


    function fetchSuggest(q) {
      if (!q || q.length < 2) { close(); return; }
      if (q === lastQ) return;
      lastQ = q;
      renderLoading(q);
      Promise.all([
        fetch(suggestUrl(q, 'product')).then(function (r) { return r.json(); }).catch(function () { return {}; }),
        fetch(suggestUrl(q, 'collection,page,article')).then(function (r) { return r.json(); }).catch(function () { return {}; })
      ])
        .then(function (parts) {
          if (input.value.trim() !== q) return; // stale: user kept typing
          var rest = resultsOf(parts[1]);
          var results = {
            products: resultsOf(parts[0]).products || [],
            collections: rest.collections || [],
            pages: rest.pages || [],
            articles: rest.articles || []
          };
          if (results.products.length) { render(results, q); return; }
          // Stufe 1+2 (UND-Varianten), danach Stufe 3: einzelne Stichwörter (ODER).
          var fallbacks = fallbackQueries(q).concat(singleWordQueries(q));
          if (!fallbacks.length) { render(results, q); return; }
          return firstFallbackHit(fallbacks, 0).then(function (hit) {
            if (!hit || input.value.trim() !== q) return; // stale: user kept typing
            if (hit.products.length) {
              results.products = hit.products;
              results.productsAreRelated = hit.related;
            }
            render(results, q);
          });
        })
        .catch(function () { close(); });
    }

    function showTerms() {
      if (!termsTemplate) return false;
      var clone = termsTemplate.content.cloneNode(true);
      dropdown.innerHTML = '';
      dropdown.appendChild(clone);
      // Wire up pill clicks
      var pills = dropdown.querySelectorAll('[data-sk-term]');
      for (var i = 0; i < pills.length; i++) {
        pills[i].addEventListener('click', function (e) {
          var term = e.currentTarget.getAttribute('data-sk-term');
          if (!term) return;
          input.value = term;
          clearBtn.hidden = false;
          lastQ = '';
          fetchSuggest(term);
          input.focus();
        });
      }
      open();
      return true;
    }

    input.addEventListener('input', function () {
      var q = input.value.trim();
      if (q) loadLexikon(); // Index (~870 KB) erst beim ersten Tippen, nicht schon beim Fokus (03.11.2026)
      clearBtn.hidden = !q;
      if (debounceId) clearTimeout(debounceId);
      if (!q) {
        // Cleared mid-search: show suggestion pills again
        lastQ = '';
        showTerms();
        return;
      }
      debounceId = setTimeout(function () { fetchSuggest(q); }, 180);
    });

    input.addEventListener('focus', function () {
      var q = input.value.trim();
      if (q.length >= 2 && currentResults.length > 0) { open(); return; }
      if (!q) { showTerms(); return; }
      // Vorbelegter Suchbegriff (Ergebnisseite): Vorschläge beim Fokus nachladen
      if (q.length >= 2) { loadLexikon(); fetchSuggest(q); }
    });

    input.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (dropdown.hidden) return;
        setActive(activeIndex + 1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (dropdown.hidden) return;
        setActive(activeIndex - 1);
      } else if (e.key === 'Enter') {
        if (activeIndex >= 0) {
          var items = dropdown.querySelectorAll('.sk-live-search__item');
          if (items[activeIndex]) {
            e.preventDefault();
            window.location.href = items[activeIndex].getAttribute('href');
          }
        }
        // otherwise, let the form submit naturally to /search?q=...
      } else if (e.key === 'Escape') {
        close();
        input.blur();
      }
    });

    // Hide/show entire search bar (Snocks-style). Only on mobile — desktop always shows.
    var MQL_MOBILE = window.matchMedia('(max-width: 768px)');
    var STORAGE_KEY = 'sk_search_hidden';
    var toggleBtn = document.querySelector('[data-mobile-search-toggle]');

    function hideBar() {
      root.classList.add('sk-search-hidden');
      if (toggleBtn) toggleBtn.removeAttribute('hidden');
      try { localStorage.setItem(STORAGE_KEY, '1'); } catch (_) {}
      close();
    }
    function showBar() {
      root.classList.remove('sk-search-hidden');
      if (toggleBtn) toggleBtn.setAttribute('hidden', '');
      try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
      input.focus();
    }

    // X button hides the entire search bar on mobile. On desktop: just clear input.
    clearBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      input.value = '';
      lastQ = '';
      clearBtn.hidden = true;
      if (MQL_MOBILE.matches) {
        hideBar();
      } else {
        showTerms();
      }
    });

    // Magnifier icon in header brings search back
    if (toggleBtn) {
      toggleBtn.addEventListener('click', function (e) {
        e.preventDefault();
        showBar();
      });
    }

    // On boot: restore hidden state from localStorage (only on mobile)
    if (MQL_MOBILE.matches) {
      try {
        if (localStorage.getItem(STORAGE_KEY) === '1') {
          root.classList.add('sk-search-hidden');
          if (toggleBtn) toggleBtn.removeAttribute('hidden');
        }
      } catch (_) {}
    }

    // Viewport crossing 768 → un-hide on desktop
    var handleMq = function () {
      if (!MQL_MOBILE.matches) {
        root.classList.remove('sk-search-hidden');
        if (toggleBtn) toggleBtn.setAttribute('hidden', '');
      } else {
        try {
          if (localStorage.getItem(STORAGE_KEY) === '1') {
            root.classList.add('sk-search-hidden');
            if (toggleBtn) toggleBtn.removeAttribute('hidden');
          }
        } catch (_) {}
      }
    };
    if (typeof MQL_MOBILE.addEventListener === 'function') MQL_MOBILE.addEventListener('change', handleMq);
    else if (typeof MQL_MOBILE.addListener === 'function') MQL_MOBILE.addListener(handleMq);

    document.addEventListener('click', function (e) {
      if (!root.contains(e.target)) close();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !dropdown.hidden) close();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
